/**
 * Hook de Socket.IO para recibir actualizaciones en tiempo real de formularios.
 * Conecta directamente con Express en el mismo origen que la web.
 *
 * Gestiona automáticamente:
 * - Espera inicial al wsToken (puede no estar disponible al montar).
 * - Reconexión con token renovado cuando el JWT expira (~15 min).
 * - Back-off exponencial para no saturar el servidor.
 * - Redirección a login cuando la sesión es irrecuperable.
 */
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getWsToken, setWsToken } from '@/services/wsToken';
import { endSessionAndGoToLogin, refreshSession } from '@/services/sessionRefresh';
const BASE_RETRY_MS = 2_000;
const MAX_RETRY_MS = 30_000;
const MAX_CONSECUTIVE_FAILURES = 5;

export type FormsUpdatedPayload = {
    action: 'create' | 'update' | 'delete' | 'bulk_delete';
    data: Record<string, unknown>;
    embarkationId: string;
    timestamp: number;
};

type UpdateHandler = (data: FormsUpdatedPayload) => void;

async function fetchFreshWsToken(): Promise<string | null> {
    try {
        let res = await fetch('/me', { credentials: 'include' });
        if (res.status === 401 && (await refreshSession())) {
            res = await fetch('/me', { credentials: 'include' });
        }
        if (!res.ok) return null;
        const data = await res.json();
        if (data.wsToken) {
            setWsToken(data.wsToken);
            return data.wsToken;
        }
        return null;
    } catch {
        return null;
    }
}

async function clearSessionAndRedirect() {
    setWsToken(null);
    await endSessionAndGoToLogin();
}

export function useSocket(
    embarkationId: string | undefined,
    onUpdate: UpdateHandler,
    onReconnect?: () => void,
) {
    const socketRef = useRef<Socket | null>(null);
    const handlerRef = useRef<UpdateHandler | null>(
        typeof onUpdate === 'function' ? onUpdate : null,
    );
    handlerRef.current = typeof onUpdate === 'function' ? onUpdate : null;
    const reconnectRef = useRef<(() => void) | undefined>(undefined);
    reconnectRef.current = typeof onReconnect === 'function' ? onReconnect : undefined;

    useEffect(() => {
        if (!embarkationId) return;

        let cancelled = false;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let failures = 0;
        let hasConnectedBefore = false;

        function cleanup() {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        }

        function connect(wsUrl: string, token: string) {
            if (cancelled) return;
            cleanup();

            if (typeof io !== 'function') {
                console.error('[useSocket] socket.io-client no exportó una función io válida (¿bundle corrupto?)');
                return;
            }

            const socket = io(wsUrl, {
                transports: ['websocket', 'polling'],
                autoConnect: false,
                reconnection: false,
                auth: { token },
            });
            socketRef.current = socket;

            socket.on('connect', () => {
                failures = 0;
                socket.emit('join-embarkation', embarkationId);
                if (hasConnectedBefore) {
                    reconnectRef.current?.();
                }
                hasConnectedBefore = true;
            });

            socket.on('session-invalidated', () => {
                cancelled = true;
                cleanup();
                clearSessionAndRedirect();
            });

            socket.on('forms-updated', (data) => {
                const fn = handlerRef.current;
                if (typeof fn === 'function') {
                    try {
                        fn(data);
                    } catch (e) {
                        console.error('[useSocket] forms-updated handler', e);
                    }
                }
            });

            socket.on('disconnect', (reason) => {
                if (cancelled || reason === 'io client disconnect') return;
                if (reason === 'io server disconnect') {
                    cancelled = true;
                    clearSessionAndRedirect();
                    return;
                }
                scheduleReconnect();
            });

            socket.on('connect_error', () => {
                if (cancelled) return;
                scheduleReconnect();
            });

            socket.connect();
        }

        function scheduleReconnect() {
            if (cancelled || reconnectTimer) return;

            failures++;
            if (failures > MAX_CONSECUTIVE_FAILURES) {
                clearSessionAndRedirect();
                return;
            }

            const delay = Math.min(BASE_RETRY_MS * 2 ** (failures - 1), MAX_RETRY_MS);
            reconnectTimer = setTimeout(async () => {
                reconnectTimer = null;
                if (cancelled) return;
                const token = await fetchFreshWsToken();
                if (token && !cancelled) {
                    connect(window.location.origin, token);
                } else if (!cancelled) {
                    scheduleReconnect();
                }
            }, delay);
        }

        async function init() {
            if (cancelled) return;

            let token = getWsToken();
            if (!token) {
                token = await fetchFreshWsToken();
            }
            if (token && !cancelled) {
                connect(window.location.origin, token);
            } else if (!cancelled) {
                scheduleReconnect();
            }
        }

        init();

        return () => {
            cancelled = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            cleanup();
        };
    }, [embarkationId]);

    return socketRef;
}
